import { useEffect, useRef, useState, useCallback } from 'react';

// --------------------------------------------------------------------------
// Auto-logout por inactividad — higiene de sesión para estaciones compartidas.
//
// Causa raíz que ataca: cuando una sesión queda abierta con un usuario y otra
// persona la usa, las acciones se registran con el usuario equivocado. Cerrar la
// sesión tras N minutos de inactividad obliga al siguiente a re-autenticarse, de
// modo que la identidad de la sesión vuelve a coincidir con la persona real.
//
// Muestra un aviso `warnMs` antes de cerrar; cualquier actividad (o el botón
// "Seguir conectado") reinicia el contador. Solo se monta en el shell autenticado.
// --------------------------------------------------------------------------

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export default function useIdleLogout({
    onLogout,
    timeoutMs = 20 * 60 * 1000, // 20 min de inactividad total
    warnMs = 60 * 1000,         // aviso 60 s antes de cerrar
    enabled = true,
}) {
    const [warning, setWarning] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(Math.floor(warnMs / 1000));

    const logoutTimer = useRef(null);
    const warnTimer = useRef(null);
    const countdown = useRef(null);

    // Guardamos onLogout en una ref para que el ciclo de timers no dependa de que el
    // caller memoice la función (si no, cada render reiniciaría el contador de inactividad).
    const onLogoutRef = useRef(onLogout);
    onLogoutRef.current = onLogout;

    const clearAll = useCallback(() => {
        clearTimeout(logoutTimer.current);
        clearTimeout(warnTimer.current);
        clearInterval(countdown.current);
    }, []);

    const reset = useCallback(() => {
        clearAll();
        setWarning(false);
        if (!enabled) return;
        warnTimer.current = setTimeout(() => {
            setWarning(true);
            setSecondsLeft(Math.floor(warnMs / 1000));
            countdown.current = setInterval(() => {
                setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
            }, 1000);
        }, Math.max(0, timeoutMs - warnMs));
        logoutTimer.current = setTimeout(() => {
            clearAll();
            onLogoutRef.current();
        }, timeoutMs);
    }, [clearAll, enabled, timeoutMs, warnMs]);

    useEffect(() => {
        if (!enabled) {
            clearAll();
            return undefined;
        }
        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset(); // arranca el ciclo
        return () => {
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset));
            clearAll();
        };
    }, [enabled, reset, clearAll]);

    const stayActive = useCallback(() => reset(), [reset]);

    return { warning, secondsLeft, stayActive };
}
